import { customElement, property, query, state } from "lit/decorators.js";
import { Accordion, AccordionItem, actions, ComponentId, ComponentStore, dom, List, ListItemRenderer, MenuEvent, Overlay } from "@omegagrid/core";
import { html, TemplateResult } from "lit";
import { AppComponent } from "../component";
import { style, itemStyle } from "./sqlParameterList.style";
import { SqlParameterListAdapter } from "./sqlParameterListAdapter";
import { Parameter } from "@reporting/rp-types/etl";
import { msg } from "@omegagrid/localize";
import { CustomParameter, createCustomParameterFromSqlParameter } from "../components/customParameter";

const SIDEBAR_FORM_WIDTH = 400;

const toolbarItems = [{
	type: 'button',
	id: 'refresh',
	color: 'gray',
	icon: {icon: 'arrows-rotate'}
}, {
	type: 'button',
	id: 'add',
	text: 'Parameter',
	color: 'blue',
	icon: {icon: 'plus'}
}];

@customElement('rp-sql-parameters')
export class SqlParameterList extends AppComponent {

	static styles = [style];

	@property({type: Object})
	adapter: SqlParameterListAdapter;

	@state()
	groups: AccordionItem[];

	@query('og-accordion')
	accordion: Accordion;

	@query('og-overlay')
	loader: Overlay;

	private _parameterComponents = new Map<string, CustomParameter>();
	get parameterComponents() { return this._parameterComponents }

	private globalParameters: Parameter[];
	private localParameters: Parameter[];

	get readonly() { return this.adapter?.isReadonly() }

	_onMenuSelect = async (e: MenuEvent, p: Parameter) => {
		switch (e.item.key) {
			case 'edit':
				(await this.host.openModalSidebar({
					id: this.adapter.createComponentId('form', p),
					position: 'right',
					caption: `@${p.name}`,
					width: SIDEBAR_FORM_WIDTH
				})).addEventListener('form.save', () => this.refresh(), {once: true});
				break;
			case 'delete':
				if ((await this.host.openMenu({
					text: `Are you sure you want to delete '@${p.name}'?`,
					content: 'yes_no'
				})).id == 'Y') {
					await this.adapter.delete(p);
					this.refresh();
				}
				break;
			case 'copy':
				navigator.clipboard.writeText(`@${p.name}`);
				break
			case 'sqlContent':
				(await this.host.open(this.adapter.createComponentId('contentEditor', p), {
					title: `@${p.name}`
				})); //.addEventListener('form.save', () => this.refresh(), {once: true});
				break;
			case 'sqlValue':
				(await this.host.open(this.adapter.createComponentId('valueEditor', p), {
					title: `@${p.name}`
				})); //.addEventListener('form.save', () => this.refresh(), {once: true});
				break;
		}
	}

	getParameterValues() {
		const values: Record<string, unknown> = {};
		this.parameterComponents.forEach((param, name) => values[name] = param.value);
		return values;
	}

	createListItemRenderer(items: Parameter[]): ListItemRenderer {
		return (div: HTMLDivElement, index: number): string | TemplateResult<1> => {
			const p = items[index];
			const menuItems = [
				{key: 'edit', value: msg('Edit'), icon: 'pen'},
				p.default_value_sql ? {key: 'sqlValue', value: 'SQL value', icon: 'code'} : null,
				p.id_thread_list == -1 ? {key: 'sqlContent', value: 'SQL content', icon: 'code'} : null,
				{key: 'copy', value: msg('Copy variable name'), icon: 'clipboard'},
				{key: 'delete', value: msg('Delete'), icon: 'trash', color: 'red'}
			].filter(x => x);

			const paramComponent = createCustomParameterFromSqlParameter(p, null, {mode: 'dev'});
			paramComponent.treeAdapter = this.adapter.getTreeAdapter(p);
			this._parameterComponents.set(p.name, paramComponent);

			return html`
				<div class="parameter-line-1">
					<div class="parameter-def ${p.description ? 'has-description' : ''}">
						${p.ord_num > 0 ? html`[${p.ord_num}] ` : ``}@${p.name} ${p.data_type}
					</div>
					${!this.readonly ? html`
						<div class="parameter-menu">
							<og-dropdown-menu
								data-idz="${p.idz}"
								data-id-thread="${p.id_thread}"
								data-name="${p.name}"
								id="menu"
								noCaret
								dynamicWidth
								alignment="right"
								color="transparent"
								style="flex: 0; min-width: 25px"
								.items="${menuItems}"
								@menu.select="${(e: MenuEvent) => this._onMenuSelect(e, p)}">
								<div slot="label"><og-icon .icon="${{icon: "ellipsis"}}"></og-icon></div>
							</og-dropdown-menu>
						</div>
					` : ``}
				</div>

				<div class="parameter-line-2">${paramComponent}</div>
			`;
		};
	}

	components = new ComponentStore((id: ComponentId) => {
		const items = id == 'globalParams' ? this.globalParameters : this.localParameters;
		return dom.createElement<List>('og-list', {
			disabledSelect: true,
			customStyle: itemStyle,
			itemHeight: 46,
			itemRenderer: this.createListItemRenderer(items),
			size: items.length
		});
	});

	showLoader = () => this.loader.show(this);
	hideLoader = () => this.loader.hide();

	async updated(props: Map<PropertyKey, unknown>) {
		if (props.has('adapter') && this.adapter) {
			await this.refresh();
		}
	}

	getAccordionItems() {
		const items: AccordionItem[] = [];
		if (this.localParameters?.length > 0) items.push({id: 'localParams', title: msg('Local parameters')});
		if (this.globalParameters?.length > 0) items.push({id: 'globalParams', title: msg('Global parameters')});
		return items;
	}

	async add() {
		(await this.host.openModalSidebar({
			id: this.adapter.createComponentId('form'),
			position: 'right',
			caption: msg('New parameter'),
			width: SIDEBAR_FORM_WIDTH
		})).addEventListener('form.save', () => this.refresh(), {once: true});
	}

	async refresh() {
		this.showLoader();

		this.components.clear();
		const params = await this.adapter.getParameters();
		this.globalParameters = params.filter(p => p.global);
		this.localParameters = params.filter(p => !p.global);

		this.requestUpdate();
		await this.updateComplete;
		this.accordion?.requestUpdate();
		this.hideLoader();
	}

	async _onToolbarAction(e: actions.ActionEvent) {
		switch (e.action.key) {
			case 'add': this.add(); break;
			case 'refresh': this.refresh(); break;
		}
	}

	layout() {
		this.accordion?.layout();
	}

	render = () => {
		const items = this.getAccordionItems();
		return html`
			<div class="controls">
				<og-button icon="square-caret-right" @click="${() => this.dispatchEvent(new CustomEvent('collapse'))}" color="transparent"></og-button>
				<div style="flex: 1"></div>
				${!this.readonly ? html`<og-button icon="plus" @click="${this.add}" color="transparent"></og-button>` : ''}
				<og-button icon="arrows-rotate" @click="${this.refresh}" color="transparent"></og-button>
			</div>

			<div class="body">
				${items.length > 0 ? html`
					<og-accordion
						.createComponent="${(id: ComponentId) => this.components.get(id)}"
						.items="${items}">
					</og-accordion>
				` : ``}
			</div>

			<og-overlay defaultLoader></og-overlay>
		`;
	}
}
